import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Leaf, Moon, Brain, Heart, Users, Briefcase, Quote, ChevronDown, Plus, Minus } from "lucide-react";
import axios from "axios";

import { API } from "@/apiConfig";

const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=1920&auto=format&fit=crop";
const DEFAULT_ABOUT_IMAGE = "https://images.unsplash.com/photo-1618919306916-94a2098b21b0?q=85&w=800&auto=format&fit=crop";

const DEFAULT_SERVICES = [
  { icon: Brain, title: "Gestion du stress", desc: "Techniques pour apaiser l'esprit et retrouver la sérénité au quotidien.", href: "/accompagnements" },
  { icon: Moon, title: "Troubles du sommeil", desc: "Retrouver un sommeil profond et réparateur grâce à des pratiques douces.", href: "/accompagnements" },
  { icon: Heart, title: "Développement personnel", desc: "Explorer votre potentiel intérieur et renforcer la confiance en soi.", href: "/accompagnements" },
  { icon: Leaf, title: "Gestion de l'anxiété", desc: "Apprivoiser l'anxiété avec bienveillance et des outils concrets.", href: "/accompagnements" },
  { icon: Users, title: "Accompagnement enfants", desc: "Des séances adaptées pour aider les enfants à s'épanouir sereinement.", href: "/accompagnements" },
  { icon: Briefcase, title: "Sophrologie en entreprise", desc: "Ateliers collectifs pour améliorer le bien-être au travail.", href: "/accompagnements" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: "easeOut" },
});

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [faq, setFaq] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [page, setPage] = useState({
    hero_title: "Retrouvez l'harmonie de votre être",
    hero_subtitle: "Accompagnement personnalisé pour la gestion du stress, le sommeil, et l'épanouissement personnel. À vos côtés à chaque étape.",
    hero_kicker: "Sophrologie Caycédienne • Vanves",
    hero_image: DEFAULT_HERO_IMAGE,
    hero_image_alt: "Paysage apaisant",
    primary_cta_label: "Prendre rendez-vous",
    secondary_cta_label: "Découvrir la sophrologie",
    services_title: "Comment puis-je vous aider ?",
    services_desc: "Chaque accompagnement est adapté à vos besoins et à votre rythme. La sophrologie s'adresse à tous.",
    about_image: DEFAULT_ABOUT_IMAGE,
    about_image_alt: "Sophrologue Eudemonie",
    about_kicker: "À propos",
    about_title: "Une approche humaine et bienveillante",
    about_text1: "Sophrologue caycédienne certifiée, j'accompagne les personnes souhaitant améliorer leur qualité de vie grâce à des outils concrets et adaptés.",
    about_text2: "Ma pratique s'appuie sur la sophrologie caycédienne, fondée par le Dr Alfonso Caycedo. Ensemble, nous travaillons sur la conscience corporelle, la relaxation dynamique et la visualisation positive.",
    about_button_label: "Mon parcours",
    about_quote: "\"La sophrologie, c'est la science de la conscience heureuse.\"",
    about_quote_author: "— Dr. Alfonso Caycedo",
    faq_kicker: "FAQ",
    faq_title: "Questions fréquentes",
    faq_desc: "Tout ce que vous devez savoir avant de commencer votre parcours.",
    faq_button_label: "Poser une question",
    cta_title: "Prêt(e) à commencer votre chemin ?",
    cta_subtitle: "Réservez votre première séance et faites le premier pas vers le bien-être.",
    cta_primary_label: "Prendre rendez-vous",
    cta_secondary_label: "Me contacter",
  });

  useEffect(() => {
    document.title = "Eudemonie — Sophrologie Caycédienne à Vanves";
    axios.get(`${API}/public/testimonials`).then(r => setTestimonials(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${API}/public/faq`).then(r => setFaq(Array.isArray(r.data) ? r.data.slice(0, 5) : [])).catch(() => {});
    axios.get(`${API}/public/pages/accueil`).then(r => { if (r.data?.content) setPage(prev => ({ ...prev, ...r.data.content })); }).catch(() => {});
  }, []);

  const services = DEFAULT_SERVICES.map((service, index) => ({
    ...service,
    title: page[`service${index + 1}_title`] || service.title,
    desc: page[`service${index + 1}_desc`] || service.desc,
  }));

  return (
    <div>
      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={page.hero_image || DEFAULT_HERO_IMAGE} alt={page.hero_image_alt || "Paysage apaisant"} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-transparent" />
        </div>

        <div className="relative section-container w-full pt-24 pb-16">
          <div className="max-w-2xl">
            <motion.p {...fadeUp(0)} className="font-manrope text-sage-light text-sm tracking-widest uppercase mb-4">
              {page.hero_kicker}
            </motion.p>
            <motion.h1 {...fadeUp(0.1)} className="font-playfair text-5xl md:text-6xl lg:text-7xl text-white leading-[1.1] mb-6" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.5)" }}>
              {page.hero_title}
            </motion.h1>
            <motion.p {...fadeUp(0.2)} className="font-manrope text-white text-lg leading-relaxed mb-10 max-w-lg" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>
              {page.hero_subtitle}
            </motion.p>
            <motion.div {...fadeUp(0.3)} className="flex flex-col sm:flex-row gap-4">
              <Link to="/prendre-rendez-vous" data-testid="hero-cta-rdv" className="btn-primary inline-flex items-center justify-center gap-2">
                {page.primary_cta_label} <ArrowRight size={16} />
              </Link>
              <Link to="/sophrologie-caycedienne" data-testid="hero-cta-discover" className="rounded-full border border-white/40 text-white hover:bg-white/10 px-8 py-3.5 transition-all duration-300 font-manrope font-medium text-sm tracking-wide text-center">
                {page.secondary_cta_label}
              </Link>
            </motion.div>
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.8 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 flex flex-col items-center gap-1">
          <span className="text-xs font-manrope tracking-wider">Explorer</span>
          <ChevronDown size={16} className="animate-bounce" />
        </motion.div>
      </section>

      {/* INTRO STRIP */}
      <section className="bg-sage text-white py-10">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { num: page.stat1_num || "10+", label: page.stat1_label || "Années d'expérience" },
              { num: page.stat2_num || "200+", label: page.stat2_label || "Personnes accompagnées" },
              { num: page.stat3_num || "95%", label: page.stat3_label || "Taux de satisfaction" },
            ].map((s, i) => (
              <motion.div key={i} {...fadeUp(i * 0.1)} data-testid={`stat-${i}`}>
                <div className="font-playfair text-4xl text-white">{s.num}</div>
                <div className="font-manrope text-sm text-white/70 mt-1">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="section-gap bg-bone-light">
        <div className="section-container">
          <motion.div {...fadeUp()} className="text-center mb-16">
            <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">Accompagnements</p>
            <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">{page.services_title}</h2>
            <p className="font-manrope text-charcoal-muted max-w-xl mx-auto leading-relaxed">
              {page.services_desc}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div key={i} {...fadeUp(i * 0.07)} data-testid={`service-card-${i}`}>
                <Link to={s.href} className="card-soft flex flex-col gap-4 h-full group block">
                  <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center group-hover:bg-sage/20 transition-colors duration-200">
                    <s.icon size={20} className="text-sage" />
                  </div>
                  <div>
                    <h3 className="font-playfair text-xl text-charcoal mb-2">{s.title}</h3>
                    <p className="font-manrope text-sm text-charcoal-muted leading-relaxed">{s.desc}</p>
                  </div>
                  <div className="mt-auto flex items-center gap-1 text-sage text-xs font-medium font-manrope group-hover:gap-2 transition-all duration-200">
                    En savoir plus <ArrowRight size={12} />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section data-testid="about-section" className="section-gap bg-bone">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp()}>
              <div className="relative">
                <img src={page.about_image || DEFAULT_ABOUT_IMAGE} alt={page.about_image_alt || "Sophrologue Eudemonie"} className="rounded-[2rem] w-full h-[500px] object-cover shadow-xl" />
                <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-5 shadow-lg border border-sage/10 max-w-[200px]">
                  <Quote size={20} className="text-sage mb-2" />
                  <p className="font-playfair text-sm italic text-charcoal leading-snug">{page.about_quote}</p>
                  <p className="font-manrope text-xs text-charcoal-muted mt-2">{page.about_quote_author}</p>
                </div>
              </div>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{page.about_kicker}</p>
              <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-6">{page.about_title}</h2>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-4">
                {page.about_text1}
              </p>
              <p className="font-manrope text-charcoal-muted leading-relaxed mb-8">
                {page.about_text2}
              </p>
              <Link to="/a-propos" className="btn-secondary inline-flex items-center gap-2">
                {page.about_button_label} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {testimonials.length > 0 && (
        <section id="temoignages" data-testid="testimonials-section" className="section-gap scroll-mt-20 bg-bone-light">
          <div className="section-container">
            <motion.div {...fadeUp()} className="text-center mb-16">
              <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">Témoignages</p>
              <h2 className="font-playfair text-4xl md:text-5xl text-charcoal">Ce qu'ils disent</h2>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <motion.div key={t.id || i} {...fadeUp(i * 0.1)} data-testid={`testimonial-${i}`} className="card-soft">
                  <Quote size={24} className="text-sage/40 mb-4" />
                  <p className="font-manrope text-charcoal-muted leading-relaxed mb-6 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-sage/10 flex items-center justify-center">
                      <span className="font-playfair text-sage text-sm">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-manrope font-medium text-sm text-charcoal">{t.name}</p>
                      {t.role && <p className="font-manrope text-xs text-charcoal-muted">{t.role}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {faq.length > 0 && (
        <section id="faq" data-testid="faq-section" className="section-gap scroll-mt-20 bg-bone">
          <div className="section-container">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <motion.div {...fadeUp()}>
                <p className="font-manrope text-xs text-sage tracking-widest uppercase mb-3">{page.faq_kicker}</p>
                <h2 className="font-playfair text-4xl md:text-5xl text-charcoal mb-4">{page.faq_title}</h2>
                <p className="font-manrope text-charcoal-muted leading-relaxed mb-8">{page.faq_desc}</p>
                <Link to="/contact" className="btn-secondary inline-flex items-center gap-2">
                  {page.faq_button_label} <ArrowRight size={16} />
                </Link>
              </motion.div>
              <motion.div {...fadeUp(0.2)} className="space-y-3">
                {faq.map((item, i) => (
                  <div key={item.id || i} data-testid={`faq-item-${i}`} className="bg-white rounded-2xl border border-sage/10 overflow-hidden">
                    <button className="w-full flex items-center justify-between px-6 py-4 text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                      <span className="font-manrope font-medium text-sm text-charcoal pr-4">{item.question}</span>
                      {openFaq === i ? <Minus size={16} className="text-sage shrink-0" /> : <Plus size={16} className="text-sage shrink-0" />}
                    </button>
                    <AnimatePresence>
                      {openFaq === i && (
                        <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                          <p className="font-manrope text-sm text-charcoal-muted px-6 pb-4 leading-relaxed">{item.answer}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="cta-section" className="py-24 bg-sage relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-white blur-3xl" />
        </div>
        <div className="section-container relative text-center">
          <motion.h2 {...fadeUp()} className="font-playfair text-4xl md:text-5xl text-white mb-4">
            {page.cta_title}
          </motion.h2>
          <motion.p {...fadeUp(0.1)} className="font-manrope text-white/75 mb-8 max-w-md mx-auto">
            {page.cta_subtitle}
          </motion.p>
          <motion.div {...fadeUp(0.2)} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/prendre-rendez-vous" data-testid="bottom-cta-rdv" className="rounded-full bg-white text-sage-dark hover:bg-bone px-8 py-4 font-manrope font-medium text-sm transition-all duration-300 shadow-md inline-flex items-center justify-center gap-2">
              {page.cta_primary_label} <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="rounded-full border border-white/40 text-white hover:bg-white/10 px-8 py-4 font-manrope font-medium text-sm transition-all duration-300 text-center">
              {page.cta_secondary_label}
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
